/**
 * PromSetupPage — /prom/setup
 *
 * Phase 1: 60-second create form. POSTs to /api/prom/create
 * (unauthenticated quick-start), then routes to the Ready page.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, GraduationCap } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PromSetupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    school_name: '',
    event_name: 'Year 11 Prom',
    event_date: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.school_name.trim()) return toast.error('Add your school name');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/prom/create`, form);
      navigate(`/prom/${r.data.slug}/ready`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create prom — try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white px-5 py-8" data-testid="prom-setup">
      <div className="max-w-md mx-auto">
        <button
          onClick={() => navigate('/prom')}
          className="flex items-center gap-1.5 text-white/55 hover:text-white text-xs font-bold mb-6"
          data-testid="prom-setup-back"
        >
          <ArrowLeft size={14} weight="bold" /> Back
        </button>

        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#B89762]/30/10 border border-[#B89762]/40/30 text-[#C29D52]/85 text-[10px] font-black uppercase tracking-[0.18em] mb-3">
          <GraduationCap size={12} weight="fill" /> Prom setup
        </div>
        <h1 className="text-3xl font-black leading-tight mb-2">Set up your prom</h1>
        <p className="text-white/55 text-sm mb-8">Three details. We'll handle the rest.</p>

        <div className="space-y-5">
          <label className="block">
            <span className="text-white/60 text-xs font-bold uppercase tracking-wider">School name</span>
            <input
              value={form.school_name}
              onChange={(e) => setForm((f) => ({ ...f, school_name: e.target.value }))}
              placeholder="e.g. St Mary's High"
              className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-[#B89762]/40/50 outline-none"
              data-testid="prom-school-name"
            />
          </label>

          <label className="block">
            <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Event name</span>
            <input
              value={form.event_name}
              onChange={(e) => setForm((f) => ({ ...f, event_name: e.target.value }))}
              placeholder="Year 11 Prom"
              className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-[#B89762]/40/50 outline-none"
              data-testid="prom-event-name"
            />
          </label>

          <label className="block">
            <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Date</span>
            <input
              type="date"
              value={form.event_date}
              onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
              className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#B89762]/40/50 outline-none [color-scheme:dark]"
              data-testid="prom-event-date"
            />
          </label>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full mt-3 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-sm font-black text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="prom-setup-submit"
          >
            {submitting ? 'Creating…' : (<>Create my prom <ArrowRight size={14} weight="bold" /></>)}
          </button>
          <p className="text-center text-[11px] text-white/40">
            Free to set up. Pay £199 only when you're ready to go live.
          </p>
        </div>
      </div>
    </div>
  );
}
